import DocumentViewerModal from "components/DocumentViewer";
import FileUpload from "components/FileUpload";
import FileView from "components/fileView";
import DynamicFormInput from "components/form/form";
import TabNav from "components/TabNav";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { updateInternalLegalResearchRequest, updateLegalResearchRequest } from "services/legalResearchService";
import { fetchServiceStampSignatures } from "services/stampService";
import { LoadingIcon } from "utils/constant";
import { LEGAL_RESEARCH_PROSECUTOR_TAB, LEGAL_RESEARCH_PROSECUTOR_TABS, LEGAL_RESEARCH_REGISTRAR_TABS, LEGAL_RESEARCH_REQUEST_STATUS } from "utils/constantValues";
import { formatDate } from "utils/helpers";
import ProposalDocument from "./proposalDocument";
import ResearchDraft from "./researchDraft";

interface registrarLegalResearchProps {
    request: any,
}

export default function InternalRegistrarLegalResearch({ request }: registrarLegalResearchProps) {
    const { t } = useTranslation();
    const [letterRefNum, setLetterRefNum] = useState("");
    const [messenger_name, setMessengerName] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [stampStatus, setStampStatus] = useState("");
    const [proposalFile, setProposalFile] = useState();
    const { showToast } = useToast();
    const contextData = useAuth();
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLetterRefNum(e.target.value)
    }
    const [formState, setFormState] = useState({
        ...request,
        urgency_level: request.urgency_level
            ? request.urgency_level.charAt(0).toUpperCase() +
            request.urgency_level.slice(1).toLowerCase()
            : "High",
    });
    const handleFileChange = (
        file: any,
    ) => {
        setProposalFile(file);
    };

    const handleSave = async () => {
        setSubmitting(true);
        const data = new FormData();
        data.append(
            "status",
            LEGAL_RESEARCH_REQUEST_STATUS.completed.value
        );
        data.append("registry_reference_number", letterRefNum);
        data.append("messenger_name", messenger_name);
        if (proposalFile)
            data.append("research_completion_letter_file", proposalFile);

        // Attach the official service stamp signatures so the backend stamps
        // the completion-letter / amended-research PDFs.
        setStampStatus(t("FetchingOfficialStamp") || "Fetching official stamp…");
        try {
            const { signature1, signature2 } = await fetchServiceStampSignatures(contextData.token);
            if (signature1) data.append("signature1", signature1);
            if (signature2) data.append("signature2", signature2);
        } catch (stampError) {
            console.error("Failed to fetch service stamp signatures:", stampError);
        }

        try {
            setStampStatus(t("StampingDocuments") || "Stamping documents…");
            const response = await updateInternalLegalResearchRequest(contextData.token, formState.id, data);

            setFormState({
                ...response,
                urgency_level: response.urgency_level
                    ? response.urgency_level.charAt(0).toUpperCase() +
                    response.urgency_level.slice(1).toLowerCase()
                    : "High",
            });
            showToast(t("LegalResearchRespondedBackSuccess"), "success");
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToUpdateLegalResearchRequest"), "error");
        } finally {
            setSubmitting(false);
            setStampStatus("");
            setLetterRefNum("");
        }
    };

    useEffect(() => { setFormState(request) }, [request]);
    
    return (
        <div className="flex flex-col h-full flex-1 -ml-4 overflow-y-auto scrollbar-hide">
            {!formState.registry_reference_number ? (
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-4 h-fit">
                    {/* Official header */}
                    <div className="bg-gradient-to-r from-[#0A1D39] to-[#13294d] px-5 py-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-amber-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            </div>
                            <div className="min-w-0">
                                <p className="text-amber-300 text-[10px] uppercase tracking-wider font-semibold">
                                    {t("OfficeOfTheAttorneyGeneral") || "Office of the Attorney General"}
                                </p>
                                <h3 className="text-white font-bold text-base">{t("RegistrarRegistration") || "Registry Registration"}</h3>
                            </div>
                        </div>
                        <p className="text-white/70 text-xs mt-2 leading-relaxed">
                            {t("RegistrarRegistrationDescription") || "Record the official registry reference and attach the completion letter to finalize this research request."}
                        </p>
                    </div>

                    {/* Body */}
                    <div className="p-5 space-y-5">
                        <div>
                            <label htmlFor="letter_reference_number" className="block text-sm font-semibold text-[#0A1D39] mb-1.5">
                                {t("LetterReferenceNumber")} <span className="text-[#9B0505]">*</span>
                            </label>
                            <p className="text-xs text-gray-500 mb-2">
                                {t("LetterReferenceNumberHint") || "The official registry reference number assigned to this request."}
                            </p>
                            <input
                                id="letter_reference_number"
                                type="text"
                                value={letterRefNum}
                                onChange={(e) => setLetterRefNum(e.target.value)}
                                placeholder={t("LetterReferenceNumberPlaceholder")}
                                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0A1D39] focus:border-transparent text-sm"
                            />
                        </div>

                        {formState.requested_by !== "internal" &&
                            <>
                                <div>
                                    <label htmlFor="messenger_name" className="block text-sm font-semibold text-[#0A1D39] mb-1.5">
                                        {t("MessengerName")}
                                    </label>
                                    <p className="text-xs text-gray-500 mb-2">
                                        {t("MessengerNameHint") || "Name of the person collecting the official completion letter."}
                                    </p>
                                    <input
                                        id="messenger_name"
                                        type="text"
                                        value={messenger_name}
                                        onChange={(e) => setMessengerName(e.target.value)}
                                        placeholder={t("MessengerNamePlaceholder")}
                                        className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0A1D39] focus:border-transparent text-sm"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-[#0A1D39] mb-1.5">
                                        {t("ResearchCompletionLetterFile")} <span className="text-[#9B0505]">*</span>
                                    </label>
                                    <p className="text-xs text-gray-500 mb-2">
                                        {t("ResearchCompletionLetterHint") || "Upload the signed official completion letter to be delivered to the requesting body."}
                                    </p>
                                    <div className="relative">
                                        <FileUpload onChange={handleFileChange} />
                                    </div>
                                </div>
                            </>
                        }

                        {/* Stamp notice */}
                        <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3">
                            <svg className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                            <p className="text-xs text-amber-800 leading-relaxed">
                                {t("StampNotice") || "The uploaded documents will be officially stamped with the Office of the Attorney General service stamp upon submission."}
                            </p>
                        </div>

                        {/* Live stamping status */}
                        {submitting && stampStatus && (
                            <div className="flex items-center gap-2 rounded-lg bg-[#0A1D39]/5 border border-[#0A1D39]/20 p-3">
                                <span className="text-[#0A1D39]">{LoadingIcon}</span>
                                <p className="text-xs font-medium text-[#0A1D39]">{stampStatus}</p>
                            </div>
                        )}

                        <button
                            onClick={handleSave}
                            disabled={submitting}
                            className="w-full flex items-center justify-center gap-2 bg-[#0A1D39] hover:bg-[#13294d] disabled:opacity-60 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
                        >
                            {submitting ? (
                                <>{LoadingIcon} {stampStatus || t("Submitting")}</>
                            ) : (
                                <>
                                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                    </svg>
                                    {t("CompleteRegistration") || "Complete Registration"}
                                </>
                            )}
                        </button>
                    </div>
                </div>
            ) : (
                /* Completed state */
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden mb-4 h-fit">
                    <div className="bg-gradient-to-r from-[#0A1D39] to-[#13294d] px-5 py-4 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center flex-shrink-0">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <div>
                            <p className="text-amber-300 text-[10px] uppercase tracking-wider font-semibold">
                                {t("OfficeOfTheAttorneyGeneral") || "Office of the Attorney General"}
                            </p>
                            <h3 className="text-white font-bold text-base">{t("RegistrationCompleted") || "Registration Completed"}</h3>
                        </div>
                    </div>
                    <div className="p-5">
                        <p className="text-[11px] uppercase tracking-wide text-gray-400 font-medium mb-1">{t("LetterReferenceNumber")}</p>
                        <p className="text-sm font-semibold text-[#0A1D39]">{formState.registry_reference_number}</p>
                    </div>
                </div>
            )}
        </div>
    );
}